import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_MENU_DEFAULT_OPTIONS } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { debounceTime } from 'rxjs/operators';
import { AlertsService } from '../../../common/components/alerts/services/alerts.service';
import { IRoutingParams } from '../../../common/models/context.model';
import { Registry } from '../../../common/models/registry.model';
import { isAdminOrManagerOrClubNetAdminOrClubAdmin, isClubManager } from '../../../common/models/user-info.model';
import { ApiV2Service } from '../../../common/services/api-v2.service';
import { AuthService } from '../../../common/services/auth.service';
import { ContextService } from '../../../common/services/context.service';
import { LocalPreloader, PreloaderService } from '../../../common/services/preloader.service';
import { ClubSettingService } from '../../club-settings/club-settings.service';

@Component({
  selector: 'app-finance-registry',
  templateUrl: './finance-registry.component.html',
  styleUrls: ['./finance-registry.component.scss'],
  providers: [{
    provide: MAT_SELECT_CONFIG,
    useValue: { overlayPanelClass: 'app-finance-registry' }
  },
  {
    provide: MAT_MENU_DEFAULT_OPTIONS,
    useValue: { overlayPanelClass: 'app-finance-registry' }
  }]
})
export class FinanceRegistryComponent implements OnInit {
  public context: IRoutingParams;
  public linkWhatsApp = 'https://wa.me/79031711653?text=%D0%97%D0%B4%D1%80%D0%B0%D0%B2%D1%81%D1%82%D0%B2%D1%83%D0%B9%D1%82%D0%B5%2C%20%D0%90%D0%BD%D0%B0%D1%81%D1%82%D0%B0%D1%81%D0%B8%D1%8F!%20%D0%9F%D0%B8%D1%88%D1%83%20%D0%B2%D0%B0%D0%BC%20%D0%BF%D0%BE%20%D0%BF%D0%BE%D0%B2%D0%BE%D0%B4%D1%83%20%D0%BE%D0%BD%D0%BB%D0%B0%D0%B9%D0%BD-%D0%BE%D0%BF%D0%BB%D0%B0%D1%82%20%D0%B2%20Sport%20Priority'
  public linkTelegram = 'https://t.me/account_sp'
  public registry: Registry[] = [];

  // -------------------------------------------------------
  public start: Date = new Date();
  public end: Date = new Date();
  public range = new FormGroup({
    start: new FormControl(this.start, [Validators.required]),
    end: new FormControl(this.end, [Validators.required]),
  });
  public showPaymentScreenToAdmin = false;
  public isExistData = false;

  public dataConsolidated: {}[];
  public consolidatedColumnNames = ['dateFrom', 'dateTo', 'dateToday', 'Paid'];
  public consolidatedColumns = [{ name: 'dateFrom', displayName: 'Дата начала' }, { name: 'dateTo', displayName: 'Дата конца' }, { name: 'dateToday', displayName: 'Дата формирования' }, { name: 'Paid', displayName: 'Оплачено' }];

  public ordersArray = [];
  public ordersArraySummary = [];
  public ordersColumnNames = [
    'paymentDate',
    'clientName',
    'paymentSource',
    'type',
    'info',
    'paymentMethod',
    'amount'
  ];
  public ordersColumns = [
    { name: 'paymentDate', displayName: 'Дата оплаты' },
    { name: 'clientName', displayName: 'Клиент' },
    { name: 'paymentSource', displayName: 'Источник оплаты' },
    { name: 'type', displayName: 'Тип' },
    { name: 'info', displayName: 'Информация о покупке' },
    { name: 'paymentMethod', displayName: 'Способ оплаты' },
    { name: 'amount', displayName: 'Оплачено' }
  ];

  public searchStringControl = new FormControl();
  public get searchString() { return this.searchStringControl.value; }
  public set searchString(value: string) { this.searchStringControl.setValue(value, { emitEvent: false }); }
  public clubSettings: any[];
  public columnFilterOptions: { [key: string]: string[] };
  public columnFilter: { [key: string]: string[] };
  public orderBy: string;
  public sortMode: string;

  public pageIndex = 0;
  public pageSize = 20;
  public totalItems = 0;

  @ViewChild('tablePaymentsRef') public tablePaymentsRef: ElementRef;

  @ViewChild('tmplPaymentPopup') tmplPaymentPopup: TemplateRef<any>;
  @ViewChild('tmplSubscriptionPopup') tmplSubscriptionPopup: TemplateRef<any>;
  @ViewChild('tmplCertificatePopup') tmplCertificatePopup: TemplateRef<any>;
  @ViewChild('tmplClubeventPopup') tmplClubeventPopup: TemplateRef<any>;

  dateFilter = (d: Date | null): boolean => {
    const date = d || new Date();
    return moment(date) <= moment();
  };

  constructor(
    private apiV2: ApiV2Service,
    private contextService: ContextService,
    private router: Router,
    private route: ActivatedRoute,
    private settingsService: ClubSettingService,
    public authService: AuthService,
    private alertsService: AlertsService,
    private readonly preloaderService: PreloaderService,
  ) {
    this.initFilterAndSort();
  }

  async ngOnInit(): Promise<void> {
    moment.locale('ru');
    this.context = this.contextService.getRoutingParams();
    await this.apiV2.registry.getByClub(this.context.clubId).then(async (registry) => {
      this.registry = registry.sort((a, b) => { return b.createdAt - a.createdAt });
    });

    // -------------------- Загрузка таблицы "Все оплаты"
    // проверка галочки "Показывать экран оплат администраторам"
    this.clubSettings  = await this.settingsService.getClubSettingsByClub();
    const alias = this.clubSettings.find(s => s.alias === 'ShowPaymentScreenToAdmin');
    // проверка на то, что это мэнэджер (администратор) клуба
    const userInfo = await this.authService.getUserInfo();

    if (isClubManager(userInfo) && alias?.value === 'true') {
      this.showPaymentScreenToAdmin = true;
    }

    if (isAdminOrManagerOrClubNetAdminOrClubAdmin(userInfo)) {
      this.showPaymentScreenToAdmin = true;
    }

    const _startDate = this.start ? moment(this.start) : null; // !!!!!
    this.range.get('start').setValue(_startDate.toDate());
    if (this.showPaymentScreenToAdmin) {
      await this.loadData();
    }

    this.searchStringControl.valueChanges.pipe(debounceTime(400)).subscribe(() => this.filterAndSortData());
  }

  // -------- Реестры -------- //

  public dateFormat(date: Date): any {
    return moment(date).format('DD.MM.YYYY');
  }

  public data(data: string, field: string): any {
    return JSON.parse(data)[field];
  }

  public formatNumber(value: number): string {
    let point = '';
    let x = String(value).replace(/(\.|,)\d+/, (m) => { point = m; return ''; });
    x = x.split('').reverse().join('')
      .replace(/(\d{3})/g, '$1 ')
      .split('').reverse().join('');
    return x + point;
  }

  onRegistry(registry: Registry) {
    this.router.navigate([`/${this.context.cityId}/${this.context.clubNetId}/${this.context.clubId}/finance/registry/${registry.id}`], { relativeTo: this.route }).then();
  }

  // -------- Все оплаты -------- //

  convertDurationUnit(subsc: { duration: number, durationUnit: number }) {
    switch (subsc?.durationUnit) {
      case 0: return `${subsc.duration} дн.`;
      case 1: return `${subsc.duration} нед.`;
      case 2: return `${subsc.duration} мес.`;
      case 3:
        const rem = subsc.duration % 10;
        return subsc.duration + (rem === 0 ? 'лет' : rem === 1 ? 'год' : rem <= 4 ? 'года' : 'лет');
      default: return 'Н/Д';
    }
  }

  /**
   * Отправляет запрос на сервер для получения актуальных данных.
   */
  @LocalPreloader<FinanceRegistryComponent>(x => x.preloaderService, a => a.tablePaymentsRef)
  public async loadData() {
    this.pageIndex = 0;
    this.initFilterAndSort();

    this.getTimeRange();
    const sd = moment(this.start).format('YYYY-MM-DDT00:00:00');
    const ed = moment(this.end).format('YYYY-MM-DDT23:59:59');

    await this.apiV2.clubPayments.getOrdersByClub(this.context.clubId, sd, ed, this.pageIndex, this.pageSize)
      .then((res) => {
        this.isExistData = false;
        this.convertOrders(res);
      });
  }

  /**
   * Отправляет запрос на сервер для получения кэшированных отфильтрованных и отсортированных данных.
   */
  @LocalPreloader<FinanceRegistryComponent>(x => x.preloaderService, a => a.tablePaymentsRef)
  public async filterAndSortData(resetPageIndex: boolean = true) {
    if (resetPageIndex) this.pageIndex = 0;

    this.getTimeRange();
    const sd = moment(this.start).format('YYYY-MM-DDT00:00:00');
    const ed = moment(this.end).format('YYYY-MM-DDT23:59:59');

    await this.apiV2.clubPayments.filterAndSortOrders(this.context.clubId, sd, ed, this.pageIndex, this.pageSize, this.searchString, this.columnFilter, this.orderBy, this.sortMode)
      .then((res) => {
        this.convertOrders(res);
      });
  }

  private async convertOrders(res: {
    amountTotal: number,
    columnFilterOptions: { [key: string]: string[] },
    items: any[],
    pagingInfo: { pageIndex: number, pageSize: number, totalItemCount: number }
  }) {
    const ordersArray = [];
    const ordersArraySummary = [];

    if (res?.items?.length) {
      let amountSummary = 0;

      res.items.forEach((item, index) => {
        const _item = this.dataParse(item, index);
        ordersArray.push(_item);
        amountSummary += Number(_item.amount);
      });

      ordersArraySummary.push({
        id: 0,
        paymentDate: 'Итого',
        amount: amountSummary.toFixed(2)
      });
    }
    this.ordersArray = ordersArray;
    this.ordersArraySummary = ordersArraySummary;

    this.columnFilterOptions = res.columnFilterOptions;

    this.pageIndex = res?.pagingInfo.pageIndex;
    this.pageSize = res?.pagingInfo.pageSize;
    this.totalItems = res?.pagingInfo.totalItemCount;

    const amountPeriod = res?.amountTotal.toFixed(2);
    this.dataConsolidated = [
      {
        id: 0,
        dateFrom: moment(this.start).format('DD.MM.YYYY'),
        dateTo: moment(this.end).format('DD.MM.YYYY'),
        dateToday: moment().format('DD.MM.YYYY'),
        Paid: amountPeriod
      }
    ];

    if (res?.items?.length) this.isExistData = true;
  }

  public dataParse(item: any, id: any) {
    return {
      id: id,

      paymentDate: item.paymentDate,
      clientName: item.clientName.replace('\n', '<br>'),
      paymentSource: item.paymentSource,
      type: item.type,
      info: item.info,
      paymentMethod: item.paymentMethod,
      amount: item.amount,

      billingData: item.billingData,
      clientInfo: item.clientInfo,
      urlClient: item.clientInfo?.id ? this.contextService.makeContextUrl(`clubclients/${item.clientInfo.id}`) : undefined,
      isExpanded: false,
      subscriptionInfo: item.subscriptionInfo,
      orderPayment: item.orderPayment,
      certInfo: item.certInfo,
      clubEventInfo: item.clubEventInfo,
      comment: item?.billingData?.comment || 'Не указано',
    };
  }

  public cellContent(elem: any, itemName: any) {
    if (elem) {
      let content = ''
      if (itemName === 'clientName' && elem['urlClient'])
        content = `<a class="btn btn-link" href="${elem['urlClient']}">${elem[itemName]}</a>`;
      else
        content = elem[itemName];

      return content.length ? content : '[не указано]';
    }
    return '';
  }

  onRangePickerChange(event: any) {
    this.getTimeRange();
    if (event.value) this.loadData();
  }

  getTimeRange() {
    if (this.range.valid) {
      this.start = this.range.get('start').value;
      this.end = this.range.get('end').value;
    }
  }

  showPopUp(item: any, e: any) {
    if (e.target.href) {
      return;
    }
    switch (item.billingData?.operationType) {
      case 6:
        this.alertsService.showConfirm({
          template: { ref: this.tmplSubscriptionPopup, context: { item } },
          buttons: []
        });
        break;
      case 8:
        this.alertsService.showConfirm({
          template: { ref: this.tmplPaymentPopup, context: { item } },
          buttons: []
        });
        break;
      case 18:
        this.alertsService.showConfirm({
          template: { ref: this.tmplClubeventPopup, context: { item } },
          buttons: []
        });
        break;
      case 19:
        this.alertsService.showConfirm({
          template: { ref: this.tmplCertificatePopup, context: { item, expiredAt: moment(item.expiredAt).format('LLL') } },
          buttons: []
        });
        break;
      default:
        if (!item.orderPayment || !item.subscriptionInfo) {
          throw new Error();
        }
        this.alertsService.showConfirm({
          template: { ref: this.tmplSubscriptionPopup, context: { item } },
          buttons: []
        });
        break;
    }
  }

  onSortChange(event: Sort) {
    this.orderBy = event.active;
    this.sortMode = event.direction.length ? event.direction : null;
    this.filterAndSortData();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.filterAndSortData(false);
  }

  alignRight(Element) {
    switch (Element) {
      case 'amount':
        return true;
    }
  }

  clearInputFilter() {
    this.searchString = '';
    this.filterAndSortData();
  }

  clearColumnFilter(columnName) {
    delete this.columnFilter[columnName];
    this.filterAndSortData();
  }

  trackByFn(index, item) {
    return item.id; // unique id corresponding to the item
  }

  initFilterAndSort() {
    this.searchString = '';
    this.columnFilter = {};
    this.orderBy = '';
    this.sortMode = null;
  }
}
